import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, AfterViewInit,
  ChangeDetectorRef, EventEmitter, Output, OnChanges, SimpleChanges
} from '@angular/core';

import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  AccordionContentTop, AccordionSize, AccordionType
} from '@design-system/components/templates/accordion/entities/accordion.types';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { GenericCardDsComponent } from '@design-system/components/templates/generic-card/generic-card.component';
import { GenericCardSpaces } from '@design-system/components/templates/generic-card/entities/generic-card.types';

@Component({
  selector: 'app-ds-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TypographyDsComponent,
    IconDsComponent,
    GenericCardDsComponent,
  ]
})
export class AccordionDsComponent implements AfterViewInit, OnChanges {

  @Input() type = AccordionType.List;
  @Input() size = AccordionSize.XS;
  @Input() icon: Icon;
  @Input() text: string;
  @Input() subtext: string;
  @Input() initState = false;
  @Input() extend: number;

  @Output() clickEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('accordionDsContent') accordionDsContent: ElementRef;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontColor = TypographyColor.Primary;

  public readonly iconColor = IconFillColor.Primary;

  public readonly genericCardSpaces = GenericCardSpaces;

  public showContent = false;
  public initHeight = 0;
  public maxHeight = 0;
  public animated = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.extend) {
      const _extend = changes?.extend?.currentValue;
      this.maxHeight = _extend ? this.maxHeight + _extend : this.initHeight;
    }
  }

  ngAfterViewInit(): void {
    this.showContent = this.initState;
    this.setMaxHeight();
  }

  get useGenericCard(): boolean {
    return this.type === AccordionType.Card;
  }

  get fontSize(): TypographyFontSize {
    switch (this.size) {
      case AccordionSize.SM:
        return TypographyFontSize.XXS;
      case AccordionSize.RG:
        return TypographyFontSize.XS;
      case AccordionSize.MD:
        return TypographyFontSize.SM;
      case AccordionSize.LG:
        return TypographyFontSize.RG;
      case AccordionSize.XL:
        return TypographyFontSize.MD;
      default:
        return TypographyFontSize.XXXS;
    }
  }

  get iconSize(): number {
    switch (this.size) {
      case AccordionSize.SM:
        return 18;
      case AccordionSize.RG:
      case AccordionSize.MD:
      case AccordionSize.LG:
      case AccordionSize.XL:
        return 20;
      default:
        return 14;
    }
  }

  get stateIcon(): Icon {
    return this.showContent ? Icon.ChevronUp : Icon.ChevronDown;
  }

  public setShowContent(): void {
    if (!this.animated) {
      this.runAnimation();
    }
    this.showContent = !this.showContent;
    this.setMaxHeight();
    this.clickEvent.emit(this.showContent);
  }

  private setMaxHeight(): void {
    this.maxHeight = this.showContent ? (this.accordionDsContent?.nativeElement?.scrollHeight ?? 0) + AccordionContentTop : 0;
    this.initHeight = this.maxHeight;
    this.cdr.detectChanges();
  }

  private runAnimation(): void {
    this.animated = true;
    this.cdr.detectChanges();
  }

}
